import { useEffect, useState, useCallback, useRef } from "react";
import { getActivityLogs, type ActivityLog, type ActivityLogsFilters } from "@/lib/api/activityLogs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Loader2, Activity, Users, FileText, UserPlus, UserCog, Clock, Filter, 
  Download, Search, ChevronLeft, ChevronRight, Calendar, RefreshCw 
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import { toast } from "sonner";

interface AuditLogViewerProps {
  workspaceId: string;
}

const activityTypeConfig: Record<string, { label: string; icon: React.ElementType; badgeVariant: "default" | "secondary" | "destructive" | "outline" }> = {
  decision_created: {
    label: "Entscheidung erstellt",
    icon: FileText,
    badgeVariant: "default",
  },
  phase_changed: {
    label: "Phase gewechselt",
    icon: Activity,
    badgeVariant: "secondary",
  },
  member_joined: {
    label: "Mitglied beigetreten",
    icon: UserPlus,
    badgeVariant: "default",
  },
  role_changed: {
    label: "Rolle geändert",
    icon: UserCog,
    badgeVariant: "outline",
  },
};

const timeFilters = [
  { value: "all", label: "Alle Zeiten" },
  { value: "today", label: "Heute" },
  { value: "week", label: "Letzte 7 Tage" },
  { value: "month", label: "Letzte 30 Tage" },
  { value: "quarter", label: "Letzte 90 Tage" },
];

const ITEMS_PER_PAGE = 25;
const SWIPE_THRESHOLD = 50;

export function AuditLogViewer({ workspaceId }: AuditLogViewerProps) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("all");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const mobileListRef = useRef<HTMLDivElement>(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const filters: ActivityLogsFilters = {};

      if (timeFilter !== "all") {
        const now = new Date();
        if (timeFilter === "today") {
          now.setHours(0, 0, 0, 0);
          filters.dateFrom = now.toISOString();
        } else if (timeFilter === "week") {
          now.setDate(now.getDate() - 7);
          filters.dateFrom = now.toISOString();
        } else if (timeFilter === "month") {
          now.setMonth(now.getMonth() - 1);
          filters.dateFrom = now.toISOString();
        } else if (timeFilter === "quarter") {
          now.setMonth(now.getMonth() - 3);
          filters.dateFrom = now.toISOString();
        }
      }

      if (selectedTypes.length > 0) {
        filters.activityTypes = selectedTypes;
      }

      const data = await getActivityLogs(workspaceId, filters);
      setLogs(data);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading activity logs:", err);
      toast.error("Aktivitätsprotokoll konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, timeFilter, selectedTypes]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Filter logs based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredLogs(logs);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = logs.filter((log) => {
      const userName = log.profiles?.name || log.profiles?.email || "";
      const metadata = JSON.stringify(log.metadata || {}).toLowerCase();
      const activityLabel = activityTypeConfig[log.activity_type]?.label || log.activity_type;
      
      return (
        userName.toLowerCase().includes(query) ||
        metadata.includes(query) ||
        activityLabel.toLowerCase().includes(query)
      );
    });

    setFilteredLogs(filtered);
    setCurrentPage(1);
  }, [logs, searchQuery]);

  const toggleActivityType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const getActivityMessage = (log: ActivityLog): string => {
    const metadata = log.metadata || {};

    switch (log.activity_type) {
      case "decision_created":
        return metadata.summary || metadata.decision_type || "Neue Entscheidung";
      case "phase_changed":
        return `${metadata.summary || "Entscheidung"}: Phase ${metadata.old_phase} → ${metadata.new_phase}`;
      case "member_joined":
        return `Als ${metadata.role === "admin" ? "Admin" : metadata.role === "owner" ? "Owner" : "Mitglied"} beigetreten`;
      case "role_changed":
        return `${metadata.target_user_name || "Mitglied"}: ${metadata.old_role} → ${metadata.new_role}`;
      default:
        return "Aktivität durchgeführt";
    }
  };

  const handleExportCSV = () => {
    const headers = ["Datum", "Uhrzeit", "Benutzer", "Email", "Aktivitätstyp", "Details", "Entity-Typ", "Entity-ID"];
    
    const rows = filteredLogs.map((log) => [
      format(new Date(log.created_at), "dd.MM.yyyy"),
      format(new Date(log.created_at), "HH:mm:ss"),
      log.profiles?.name || "-",
      log.profiles?.email || "-",
      activityTypeConfig[log.activity_type]?.label || log.activity_type,
      getActivityMessage(log),
      log.entity_type,
      log.entity_id || "-",
    ]);

    const csvContent = [
      headers.join(";"),
      ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";")),
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-log-${format(new Date(), "yyyy-MM-dd")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success(`${filteredLogs.length} Einträge exportiert`);
  };

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE);
  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Swipe handlers for mobile pagination
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    setIsSwiping(false);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current || totalPages <= 1) return;
    
    const deltaX = e.touches[0].clientX - touchStartRef.current.x;
    const deltaY = e.touches[0].clientY - touchStartRef.current.y;
    
    // Only handle horizontal swipes (ignore vertical scrolling)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      setIsSwiping(true);
      // Limit the offset for visual feedback
      const maxOffset = 100;
      const canSwipeLeft = currentPage < totalPages;
      const canSwipeRight = currentPage > 1;
      
      if ((deltaX < 0 && canSwipeLeft) || (deltaX > 0 && canSwipeRight)) {
        setSwipeOffset(Math.max(-maxOffset, Math.min(maxOffset, deltaX)));
      } else {
        // Resistance effect when can't swipe further
        setSwipeOffset(deltaX * 0.2);
      }
    }
  }, [currentPage, totalPages]);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartRef.current) return;
    
    if (Math.abs(swipeOffset) >= SWIPE_THRESHOLD) {
      if (swipeOffset < 0 && currentPage < totalPages) {
        // Swipe left = next page
        setCurrentPage((p) => p + 1);
        // Haptic feedback
        if (navigator.vibrate) navigator.vibrate(10);
      } else if (swipeOffset > 0 && currentPage > 1) {
        // Swipe right = previous page
        setCurrentPage((p) => p - 1);
        if (navigator.vibrate) navigator.vibrate(10);
      }
    }
    
    touchStartRef.current = null;
    setSwipeOffset(0);
    setIsSwiping(false);
  }, [swipeOffset, currentPage, totalPages]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Audit-Log
            </CardTitle>
            <CardDescription>
              Vollständige chronologische Übersicht aller Workspace-Aktivitäten
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadLogs}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Aktualisieren
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExportCSV}
              disabled={filteredLogs.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              CSV Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Suchen nach Benutzer, Aktivität..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                  <Filter className="h-4 w-4 mr-2" />
                  <span className="hidden xs:inline">Aktivitätstypen</span>
                  <span className="xs:hidden">Filter</span>
                  {selectedTypes.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedTypes.length}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[calc(100vw-2rem)] sm:w-80" align="start" sideOffset={8}>
                <div className="space-y-4">
                  <h4 className="font-medium text-sm">Aktivitätstypen filtern</h4>
                  <div className="space-y-3">
                    {Object.entries(activityTypeConfig).map(([type, config]) => (
                      <div key={type} className="flex items-center space-x-2">
                        <Checkbox
                          id={`audit-${type}`}
                          checked={selectedTypes.includes(type)}
                          onCheckedChange={() => toggleActivityType(type)}
                        />
                        <Label
                          htmlFor={`audit-${type}`}
                          className="text-sm font-normal cursor-pointer flex items-center gap-2"
                        >
                          <config.icon className="h-4 w-4" />
                          {config.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {selectedTypes.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedTypes([])}
                      className="w-full"
                    >
                      Alle Filter zurücksetzen
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            <Select value={timeFilter} onValueChange={setTimeFilter}>
              <SelectTrigger className="flex-1 sm:flex-none sm:w-[160px]">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeFilters.map((filter) => (
                  <SelectItem key={filter.value} value={filter.value}>
                    {filter.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results info */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {filteredLogs.length} {filteredLogs.length === 1 ? "Eintrag" : "Einträge"} gefunden
          </span>
          {totalPages > 1 && (
            <span>
              Seite {currentPage} von {totalPages}
            </span>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Keine Aktivitäten gefunden</p>
            {(searchQuery || selectedTypes.length > 0) && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setSearchQuery("");
                  setSelectedTypes([]);
                }}
                className="mt-2"
              >
                Filter zurücksetzen
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile: Card-based layout with swipe */}
            <div 
              ref={mobileListRef}
              className="sm:hidden space-y-3 touch-pan-y"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                transform: `translateX(${swipeOffset}px)`,
                transition: isSwiping ? 'none' : 'transform 0.2s ease-out',
              }}
            >
              {paginatedLogs.map((log) => {
                const config = activityTypeConfig[log.activity_type];
                const Icon = config?.icon || Activity;
                
                return (
                  <div 
                    key={log.id} 
                    className="rounded-lg border bg-card p-4 space-y-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {log.profiles?.name || "Unbekannt"}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {log.profiles?.email}
                        </p>
                      </div>
                      <Badge variant={config?.badgeVariant || "outline"} className="gap-1 shrink-0">
                        <Icon className="h-3 w-3" />
                        <span className="hidden xs:inline">{config?.label || log.activity_type}</span>
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {getActivityMessage(log)}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {format(new Date(log.created_at), "dd.MM.yyyy 'um' HH:mm", { locale: de })}
                    </p>
                  </div>
                );
              })}
              
              {/* Swipe hint for mobile */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-2 text-xs text-muted-foreground">
                  <ChevronLeft className="h-3 w-3" />
                  <span>Wischen für weitere Seiten</span>
                  <ChevronRight className="h-3 w-3" />
                </div>
              )}
            </div>

            {/* Desktop: Table layout */}
            <ScrollArea className="rounded-md border hidden sm:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Zeitpunkt</TableHead>
                    <TableHead className="w-[180px]">Benutzer</TableHead>
                    <TableHead className="w-[160px]">Aktivität</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedLogs.map((log) => {
                    const config = activityTypeConfig[log.activity_type];
                    const Icon = config?.icon || Activity;
                    
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-xs">
                          <div className="flex flex-col">
                            <span>{format(new Date(log.created_at), "dd.MM.yyyy")}</span>
                            <span className="text-muted-foreground">
                              {format(new Date(log.created_at), "HH:mm:ss")}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium truncate max-w-[150px]">
                              {log.profiles?.name || "Unbekannt"}
                            </span>
                            <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                              {log.profiles?.email}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={config?.badgeVariant || "outline"} className="gap-1">
                            <Icon className="h-3 w-3" />
                            {config?.label || log.activity_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {getActivityMessage(log)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-8"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
